import React, { useState, useRef, useEffect, lazy, Suspense } from "react";
import { motion, useInView } from "framer-motion";
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from "../i18n/LanguageProvider";
import "../css/PreSite.css";
import "../styles/StripePricingTable.css";
import Antho from "../assets/PreSiteImages/FotoCreatorAntho2.jpeg";
import Mica from "../assets/PreSiteImages/FotoCreatorMica.jpeg";
import MoviesSS from "../assets/screenshots/MoviesScreenShor.png";
import DashboardScreenShot from "../assets/screenshots/DashboardScreenShot.png";
import CalendarScreenShot from "../assets/screenshots/CalendarScreenShot.png";
import { ReactComponent as LogoSVG } from "../assets/Icons/Logotipo.svg";
import { useAuth } from "../config/authContext";

// Lazy-load the heavy Terms modal to keep initial JS small
const TermsModal = lazy(() => import("../components/TermsModal"));

const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
    animate: {
        transition: {
            staggerChildren: 0.1,
        },
    },
};

function AnimatedSection({ children, className = "" }) {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });
    return (
        <motion.div
            ref={ref}
            initial="initial"
            animate={isInView ? "animate" : "initial"}
            variants={fadeInUp}
            className={className}
        >
            {children}
        </motion.div>
    );
}

// Lightweight inline SVG icons to avoid pulling primeicons CSS
function Icon({ name, className = "icon" }) {
    switch (name) {
        case "check":
            return (
                <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" />
                </svg>
            );
        case "calendar":
            return (
                <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
            );
        case "book":
            return (
                <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M4 4v15.5A2.5 2.5 0 0 1 6.5 22H20V6a2 2 0 0 0-2-2H6.5A2.5 2.5 0 0 0 4 6.5" />
                </svg>
            );
        case "bag":
            return (
                <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6 2l1 4h10l1-4" />
                    <path d="M6 6h12l1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L6 6z" />
                    <path d="M9 10v4" />
                    <path d="M15 10v4" />
                </svg>
            );
        case "video":
            return (
                <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="4" width="15" height="16" rx="2" />
                    <path d="M17 8l5-3v14l-5-3z" />
                </svg>
            );
        case "play":
            return (
                <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M8 5v14l11-7z" />
                </svg>
            );
        default:
            return null;
    }
}

const PreSite = () => {
    const { t } = useLanguage();
    const { userLoggedIn, loading } = useAuth();
    const [email, setEmail] = useState('');
    const [showContactForm, setShowContactForm] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [contact, setContact] = useState({ name: "", email: "", message: "" });
    const navigate = useNavigate();
    const contactFormRef = useRef(null);

    const handleSubmit = (e) => {
        e.preventDefault();
        localStorage.setItem('preRegisterEmail', email);
        navigate('/register');
    };

    const handleContactChange = (e) => {
        setContact({ ...contact, [e.target.name]: e.target.value });
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        formData.append("access_key", "34d85380-4c6e-417c-b4f4-f0c4171cb347");

        const object = Object.fromEntries(formData);
        const json = JSON.stringify(object);

        const res = await fetch("https://api.web3forms.com/submit", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json"
            },
            body: json
        }).then((res) => res.json());

        if (res.success) {
            alert("Message sent! Thank you for contacting us.");
            setContact({ name: "", email: "", message: "" });
        } else {
            alert("There was an error sending your message. Please try again.");
        }
    };

    useEffect(() => {
        if (!loading && userLoggedIn) {
            navigate('/dashboard', { replace: true });
        }
    }, [loading, userLoggedIn, navigate]);

    useEffect(() => {
        if (showContactForm && contactFormRef.current) {
            contactFormRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [showContactForm]);

    return (
        <div className="container">
            {/* Header */}
            <motion.header
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="header"
            >
                <nav className="nav">
                    <div className="logo"><p>Frazza</p></div>
                    <div className="navLinks">
                        <a href="#features" className="navLink">{t("Features")}</a>
                        <a href="#pricing" className="navLink">{t("Pricing")}</a>
                        <a href="#about" className="navLink">{t("About us")}</a>
                    </div>
                    <div className="langSelect">
                        <LanguageDropdown />
                    </div>
                </nav>
            </motion.header>

            {/* Hero Section */}
            <section className="heroSection">
                <div className="heroGrid">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="heroContent"
                    >
                        <h1 className="heroTitle">
                            {t("Because Distance Shouldn't Mean Disconnection")}
                        </h1>
                        <p className="heroDescription">
                            {t("Frazza aims to be the ultimate platform for couples, providing an intuitive tool that strengthens connection, simplifies organization, and enriches time together, no matter the distance.")}
                        </p>
                        <div className="heroButtons">
                            <Link to="/auth">
                                <button className="primaryButton p-button">
                                    {t("Get Started →")}
                                </button>
                            </Link>
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="heroImage"
                    >
                        <LogoSVG
                            className="heroImg"
                            role="img"
                            aria-label="Couple connecting through phone"
                            width={600}
                            height={400}
                            focusable="false"
                        />
                    </motion.div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="featuresSection">
                <AnimatedSection className="sectionHeader">
                    <h2 className="sectionTitle">{t("Everything You Need to Stay Connected")}</h2>
                    <p className="sectionDescription">
                        {t("From shared calendars to movie nights, Frazza brings couples together with tools designed for love across any distance.")}
                    </p>
                </AnimatedSection>

                <motion.div
                    variants={staggerContainer}
                    initial="initial"
                    whileInView="animate"
                    viewport={{ once: true }}
                    className="featuresGrid"
                >
                    {[
                        {
                            title: t("Shared Todos"),
                            description: t("Keep track of tasks together. See what your partner is working on and celebrate completed goals as a team."),
                            icon: "check"
                        },
                        {
                            title: t("Synchronized Calendar"),
                            description: t("Plan visits, important dates, and shared events. Never miss an anniversary or special moment again."),
                            icon: "calendar"
                        },
                        {
                            title: t("Shared Notes"),
                            description: t("Create a digital notebook together. Share thoughts, dreams, and memories in your private space."),
                            icon: "book"
                        },
                        {
                            title: t("Recipe Discovery"),
                            description: t('Find and share recipes to cook together virtually. Make the same meal and enjoy dinner "together."'),
                            icon: "bag"
                        },
                        {
                            title: t("Movie Matching"),
                            description: t("Find movies available on both your streaming platforms. Perfect for synchronized movie nights."),
                            icon: "video"
                        },
                        {
                            title: t("Question Games"),
                            description: t("Discover new things about each other with fun question games designed to deepen your connection."),
                            icon: "play"
                        },
                    ].map((feature, index) => (
                        <motion.div key={index} variants={fadeInUp}>
                            <div className="featureCard">
                                <div className="featureContent">
                                    <div className="featureIcon">
                                        <Icon name={feature.icon} />
                                    </div>
                                    <h3 className="featureTitle">{feature.title}</h3>
                                    <p className="featureDescription">{feature.description}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </section>

            {/* Dashboard Section */}
            <section className="dashboardSection">
                <div className="dashboardGrid">
                    <AnimatedSection>
                        <h2 className="dashboardTitle">{t("Your Relationship Dashboard")}</h2>
                        <p className="dashboardDescription">
                            {t("See everything at a glance - your shared todos, upcoming visits, and relationship milestones. Track your journey together with beautiful timeline features.")}
                        </p>
                        <div className="Features">
                            <ul className="FeaturesList">
                                {[t("Track days together"), t("Milestone celebrations"), t("Visit countdown")].map((item, index) => (
                                    <li key={index} className="Feature">
                                
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </AnimatedSection>
                    <AnimatedSection>
                        <div className="dashboardCard">
                            <img src={DashboardScreenShot} alt="Dashboard screenshot" className="preSiteScreenshot" loading="lazy" decoding="async" width={960} height={600} />
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Movie Platform Section */}
            <section className="movieSection">
                <div className="movieGrid">
                    <AnimatedSection>
                        <div className="movieCard">
                            <img src={MoviesSS} alt="Movies feature screenshot"  className="preSiteScreenshot" loading="lazy" decoding="async" width={960} height={600} />
                        </div>
                    </AnimatedSection>
                    <AnimatedSection>
                        <h2 className="movieTitle">{t("Frazzinema: Your Movie Night Companion")}</h2>
                        <p className="movieDescription">
                            {t("Never struggle to find a movie you both can watch again. Our smart platform finds content available on both your streaming services, making movie nights effortless.")}
                        </p>
                        <div className="Features">
                            <ul className="FeaturesList">
                                {[t("Cross-platform search"), t("Shared watchlists"), t("Personalized recommendations")].map((item, index) => (
                                    <li key={index} className="Feature">
                                        
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </AnimatedSection>
                </div>
            </section>
            <section className="calendarSection">
                <div className="calendarGrid">
                    <AnimatedSection>
                        <h2 className="calendarTitle">{t("Shared Calendar: Plan Together, Stay Connected")}</h2>
                        <p className="calendarDescription">
                            {t("Organize your relationship with a shared calendar. Add events, schedule visits, and see your todos all in one place so you never miss a moment together.")}
                        </p>
                        <div className="Features">
                            <ul className="FeaturesList">
                                {[t("Add events together"), t("Create and track visits"), t("Display shared todos")].map((item, index) => (
                                    <li key={index} className="Feature">
                                        
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </AnimatedSection>
                    <AnimatedSection>
                        <div className="calendarCard">
                            <img src={CalendarScreenShot} alt="Calendar screenshot" className="preSiteScreenshot" loading="lazy" decoding="async" width={960} height={600} />
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            <section id="about" className="aboutSection">
                <AnimatedSection>
                    <h2 className="sectionTitle">{t("About Us")}</h2>
                    <div className="aboutContent">
                        <div className="aboutCreators">
                            <div className="aboutCreatorAntho">
                                <img src={Antho} className="aboutCreatorImg" alt="Anthonella, co-creator" loading="lazy" decoding="async" width={200} height={200} />
                            </div>
                            <div className="aboutCreatorMica">
                                <img src={Mica} className="aboutCreatorImg" alt="Micael, co-creator" loading="lazy" decoding="async" width={200} height={200} />
                            </div>
                        </div>
                        <p className="testimonial-text">
                            {t("As a long-distance couple ourselves—Micael in Winterthur, Switzerland, and Anthonella in Göttingen, Germany—we created Frazza to bring everything we wished for into one place. More than just an organizational tool, it's a space to share moments, create memories, enjoy entertainment, and make distance feel a little smaller every day.")}
                        </p>
                    </div>
                </AnimatedSection>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="pricingSection">
                <AnimatedSection className="pricingHeader">
                    <h2 className="pricingTitle">{t("Choose Your Plan")}</h2>
                    <p className="pricingDescription">{t("Start free and upgrade anytime to unlock premium features")}</p>
                </AnimatedSection>

                {/* Pricing Cards */}
                <div className="stripe-pricing-container" style={{ marginBottom: '1rem' }}>
                    {/* Free Plan */}
                    <div className="pricing-card free" onClick={() => navigate('/auth')} style={{ cursor: 'pointer' }}>
                        <div className="pricing-header free">Free Plan</div>
                        <div className="pricing-content">
                            <div className="pricing-price">$0 <span className="pricing-period">/ forever</span></div>
                            <ul className="pricing-list">
                                <li>Basic Features</li>
                                <li>Limited Access</li>
                                <li>Community Support</li>
                            </ul>
                        </div>
                    </div>

                    {/* Monthly Plan */}
                    <div className="pricing-card" onClick={() => navigate('/auth')} style={{ cursor: 'pointer' }}>
                        <div className="pricing-header">Monthly Plan</div>
                        <div className="pricing-content">
                            <div className="pricing-price">$4.99 <span className="pricing-period">/ month</span></div>
                            <ul className="pricing-list">
                                <li>Full Access to All Features</li>
                                <li>Sync with Partner</li>
                                <li>Cancel Anytime</li>
                            </ul>
                        </div>
                    </div>

                    {/* Yearly Plan */}
                    <div className="pricing-card" onClick={() => navigate('/auth')} style={{ cursor: 'pointer' }}>
                        <div className="pricing-header yearly">Yearly Plan</div>
                        <div className="pricing-content">
                            <div className="pricing-price">$49.99 <span className="pricing-period">/ year</span></div>
                            <div className="pricing-save-badge">Save 17%</div>
                            <ul className="pricing-list">
                                <li>All Monthly Features</li>
                                <li>Best Value</li>
                                <li>Priority Support</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#666', marginBottom: '3rem', fontStyle: 'italic' }}>
                    {t("subscriptions are available after creating an account")}
                </p>

                <AnimatedSection className="pricingHeader">
                    <h2 className="pricingTitle">{t("Support Frazza's Development")}</h2>
                    <p className="pricingDescription">{t("Frazza is free to use. If you enjoy it, consider buying us a coffee to help us grow and improve!")}</p>
                </AnimatedSection>

                <motion.div
                    variants={staggerContainer}
                    initial="initial"
                    whileInView="animate"
                    viewport={{ once: true }}
                    className="pricingGrid"
                >
                    <motion.div variants={fadeInUp}>
                        <div className="pricingCardPopular">
                            <div className="popularBadge">{t("Support Us")}</div>
                            <div className="pricingCardContent">
                                <h3 className="planTitle">{t("Buy Me a Coffee")}</h3>
                                <div className="coffeeContainer" style={{ textAlign: "center", margin: "1.5rem 0" }}>
                                    <a href="https://coff.ee/micaelstaem" target="_blank" rel="noopener noreferrer">
                                        <img
                                            src="https://cdn.buymeacoffee.com/buttons/v2/default-orange.png"
                                            alt={t("Buy Me a Coffee")}
                                            className="coffeeButton"
                                            width={180}
                                            height={51}
                                            loading="lazy"
                                            decoding="async"
                                            style={{ maxWidth: 180, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
                                        />
                                    </a>
                                </div>
                                <p className="planDescription" style={{fontWeight: 500, marginBottom: 20 }}>
                                    {t("If you enjoy using Frazza, consider buying us a coffee!")}
                                </p>
                                <div className="planFeatures" style={{ marginBottom: 24 }}>
                                    <ul style={{ paddingLeft: 24, margin: 0 }}>
                                        {[
                                            t("Support independent developers"),
                                            t("Help us improve Frazza"),
                                            t("Enable new features"),
                                            t("Show your appreciation")
                                        ].map((feature, index) => (
                                            <li key={index} className="planFeature" style={{ display: "flex", alignItems: "center", margin: "8px 0" }}>
                                                <span className="planFeatureIcon" style={{ marginRight: 10 }} />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </section>

            {/* CTA Section */}
            <section className="ctaSection">
                <AnimatedSection>
                    <h2 className="ctaTitle">{t("Ready to Strengthen Your Connection?")}</h2>
                    <p className="ctaDescription">
                        {t("Join thousands of couples who are making distance feel smaller with Frazza.")}
                    </p>
                    <form onSubmit={handleSubmit} className="ctaForm">
                        <input
                            type="email"
                            placeholder={t("Enter your email")}
                            className="ctaInput"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        <button type="submit" className="ctaButton p-button">{t("Start Free")}</button>
                    </form>
                    <p className="ctaNote">{t("No credit card required. Start connecting today.")}</p>
                </AnimatedSection>
            </section>

            <footer className="footer">
                {showContactForm && (
                    <div className="contactFormContainer">
                        <h2>Contact Us</h2>
                        <div className="footerContactForm" ref={contactFormRef}>
                            <form onSubmit={handleContactSubmit} className="contactForm">
                                <div>
                                    <label>{t("Full Name")}</label>
                                    <input
                                        className="contactInput"
                                        type="text"
                                        name="name"
                                        value={contact.name}
                                        onChange={handleContactChange}
                                        required
                                    />
                                </div>
                                <div>
                                    <label>{t("Email")}</label>
                                    <input
                                        className="contactInput"
                                        type="email"
                                        name="email"
                                        value={contact.email}
                                        onChange={handleContactChange}
                                        required
                                    />
                                </div>
                                <div>
                                    <label>{t("Message")}</label>
                                    <textarea
                                        className="contactInput"
                                        name="message"
                                        value={contact.message}
                                        onChange={handleContactChange}
                                        required
                                    />
                                </div>
                                <button type="submit" className="p-button">{t("Send")}</button>
                            </form>
                        </div>
                    </div>
                )}
                <div className="footerContent">
                    <div className="footerLogo">{t("Frazza")}</div>
                    <div className="footerLinks">
                        <button
                            className="footerLink"
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                            onClick={() => setShowTermsModal(true)}
                        >
                            {t("Terms and Conditions")}
                        </button>
                        <button
                            className="footerLink"
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                            onClick={() => setShowContactForm((v) => !v)}
                        >
                            {t("Contact Us")}
                        </button>
                    </div>
                </div>
            </footer>

            {/* Terms and Conditions Modal */}
            {showTermsModal && (
                <Suspense fallback={<div className="modal-overlay"><div className="modal-content" style={{padding: '2rem'}}>Loading…</div></div>}>
                    <TermsModal onClose={() => setShowTermsModal(false)} />
                </Suspense>
            )}
        </div>
    );
};

export default PreSite;

const languages = [
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "de", label: "Deutsch" },
];

export function LanguageDropdown() {
    const { language, setLanguage } = useLanguage();
    const [open, setOpen] = useState(false);
    const ref = useRef();

    useEffect(() => {
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="customLangDropdown" ref={ref}>
            <button
                className="dropdownToggle"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {languages.find((l) => l.code === language)?.label}
            </button>
            {open && (
                <ul className="dropdownMenu" role="listbox">
                    {languages.map((lang) => (
                        <li
                            key={lang.code}
                            className={`dropdownItem${lang.code === language ? " selected" : ""}`}
                            onClick={() => {
                                setLanguage(lang.code);
                                setOpen(false);
                            }}
                            role="option"
                            aria-selected={lang.code === language}
                        >
                            {lang.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}